/**
 * Cliente API Robusto con manejo de errores, retry logic y logging
 */
import axios from 'axios';
import errorLogger from './errorHandler';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request Interceptor: Agregar logging y headers
apiClient.interceptors.request.use(
  (config) => {
    errorLogger.info(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      data: config.data
    });
    
    // Agregar token de autenticación si existe
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    errorLogger.log(error, { type: 'REQUEST_INTERCEPTOR_ERROR' });
    return Promise.reject(error);
  }
);

// Response Interceptor: Manejo de errores y logging
apiClient.interceptors.response.use(
  (response) => {
    errorLogger.info(`API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      data: response.data
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log del error
    errorLogger.log(error, {
      type: 'API_ERROR',
      url: originalRequest?.url,
      method: originalRequest?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    });

    // Retry logic para errores de red
    if (!error.response && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      if (originalRequest._retryCount < 3) {
        errorLogger.warn(`Reintentando petición (${originalRequest._retryCount}/3): ${originalRequest.url}`);
        // Esperar antes de reintentar (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * originalRequest._retryCount));
        return apiClient(originalRequest);
      }
    }

    // Manejo de errores específicos por código de estado
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // No autorizado - limpiar token y redirigir
          localStorage.removeItem('auth_token');
          errorLogger.warn('Sesión expirada, redirigiendo...');
          // window.location.href = '/login';
          break;
        
        case 403:
          errorLogger.warn('Acceso denegado');
          break;
        
        case 404:
          errorLogger.warn('Recurso no encontrado', { url: originalRequest?.url });
          break;
        
        case 429:
          errorLogger.warn('Demasiadas peticiones, espera un momento');
          break;
        
        case 500:
        case 502:
        case 503:
          errorLogger.log(new Error('Error del servidor'), {
            type: 'SERVER_ERROR',
            status,
            url: originalRequest?.url
          });
          break;
        
        default:
          errorLogger.log(new Error(data?.message || 'Error desconocido'), {
            type: 'API_ERROR',
            status
          });
      }
    }

    return Promise.reject(error);
  }
);

// Helper functions para diferentes tipos de peticiones
export const api = {
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

export default apiClient;
